import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDown, BookOpen, Target, X, Loader2, FileText, ExternalLink } from 'lucide-react';
import API_ENDPOINTS from '../config/api';
import ModalWrapper from './ModalWrapper';
import EvaluationTagChips from './EvaluationTagChips';
import { extractTagEvaluation, hasLegacyNumericScores } from '../utils/evaluationDisplay';
import SourceTag from './AIReferences/SourceTag';
import { TIER_ORDER, getTier } from './AIReferences/tiers';
import { useTranslation } from '../hooks/useTranslation.jsx';

// Strip PDF/image conversion artifacts from RAG chunk content
function cleanChunkContent(text) {
  if (!text) return '';
  return text
    .replace(/^\*\*Kuva\*\*:\s*`[^`]+`\s*$/gm, '')       // **Kuva**: `filename.png`
    .replace(/^>\s*\*\*(Kuvatiedosto|Muunnettu tekstiksi|Malli)\*\*:.*$/gm, '') // > **Kuvatiedosto**: ...
    .replace(/^#{1,3}\s*Kuvateksti\s*$/gm, '')             // ### Kuvateksti
    .replace(/^#{1,3}\s*Comprehensive Analysis.*$/gm, '')   // ### Comprehensive Analysis of the Image
    .replace(/^---+$/gm, '')                                // --- dividers
    .replace(/\n{3,}/g, '\n\n')                             // collapse excess newlines
    .trim();
}

// --- Layout ---

const Container = styled.div`
  width: 100%;
`;

const TabRow = styled.div`
  display: flex;
  gap: 2px;
`;

const Tab = styled.button`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 7px 10px;
  background: ${props => props.$active
    ? 'rgba(91, 157, 217, 0.12)'
    : 'rgba(255, 255, 255, 0.03)'};
  border: 1px solid ${props => props.$active
    ? 'rgba(91, 157, 217, 0.25)'
    : 'rgba(255, 255, 255, 0.08)'};
  border-bottom: ${props => props.$active ? 'none' : '1px solid rgba(255, 255, 255, 0.08)'};
  border-radius: 4px 4px 0 0;
  color: ${props => props.$active
    ? 'rgba(255, 255, 255, 0.85)'
    : 'rgba(255, 255, 255, 0.45)'};
  font-size: 0.72rem;
  font-family: 'Rational', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.7);
    background: ${props => props.$active
      ? 'rgba(91, 157, 217, 0.12)'
      : 'rgba(255, 255, 255, 0.06)'};
  }

  svg.chevron {
    width: 11px;
    height: 11px;
    transition: transform 0.2s ease;
    transform: rotate(${props => props.$active ? '180deg' : '0deg'});
    flex-shrink: 0;
  }
`;

const BarInline = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.08);
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
`;

const BarFillInline = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
  ${props => props.$bipolar ? `
    left: ${props.$value < 0 ? `${50 + props.$value / 2}%` : '50%'};
    width: ${Math.abs(props.$value) / 2}%;
    background: ${props.$value < 0 ? '#E94B6E' : '#4CAF50'};
  ` : `
    left: 0;
    width: ${props.$value}%;
    background: ${props.$value >= 70 ? '#4CAF50' : props.$value >= 40 ? '#81C784' : '#E94B6E'};
  `}
`;

const BarCenterLine = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
  width: 1px;
  height: 100%;
  background: rgba(255, 255, 255, 0.2);
`;

const ValueBadge = styled.span`
  font-weight: 600;
  font-size: 0.65rem;
  color: ${props => props.$color};
  min-width: 18px;
  text-align: right;
`;

const ContentArea = styled.div`
  background: rgba(91, 157, 217, 0.06);
  border: 1px solid rgba(91, 157, 217, 0.15);
  border-top: none;
  border-radius: 0 0 4px 4px;
  padding: 12px 14px;
`;

const ReasonText = styled.div`
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.5;
`;

const RefItem = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  font-size: 0.78rem;
  color: ${props => props.$expanded ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.6)'};
  line-height: 1.4;
  cursor: pointer;
  padding: 3px 4px;
  margin: -3px -4px;
  border-radius: 3px;
  transition: background 0.1s ease;
  background: ${props => props.$expanded ? 'rgba(255, 255, 255, 0.04)' : 'transparent'};
  &:hover {
    color: rgba(255, 255, 255, 0.85);
    background: rgba(255, 255, 255, 0.04);
  }
`;

const RefNumber = styled.span`
  color: ${props => props.$analysis ? '#FFB74D' : '#5B9DD9'};
  font-weight: 600;
  flex-shrink: 0;
  font-size: 0.72rem;
`;

const RefDivider = styled.div`
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 6px 0 2px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin-top: 4px;
`;

const RefCitation = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RefRelevance = styled.span`
  font-size: 0.68rem;
  color: #5B9DD9;
  background: rgba(91, 157, 217, 0.12);
  padding: 1px 5px;
  border-radius: 3px;
  flex-shrink: 0;
  font-weight: 500;
`;

const RefList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

// --- Inline chunk expansion ---

const InlineChunkArea = styled.div`
  margin: 4px 0 8px 0;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid rgba(91, 157, 217, 0.12);
`;

const ChunkBlock = styled.div`
  font-size: 0.78rem;
  line-height: 1.55;
  white-space: pre-wrap;
  padding: 8px 12px;
  color: ${props => props.$isTarget ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.3)'};
  background: ${props => props.$isTarget ? 'rgba(91, 157, 217, 0.08)' : 'transparent'};
  border-left: 2px solid ${props => props.$isTarget ? '#5B9DD9' : 'rgba(255, 255, 255, 0.04)'};
`;

const InlineFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.3);
`;

const OpenDocButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px solid rgba(91, 157, 217, 0.2);
  border-radius: 3px;
  color: #5B9DD9;
  font-size: 0.7rem;
  padding: 3px 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    background: rgba(91, 157, 217, 0.1);
    border-color: rgba(91, 157, 217, 0.4);
  }
  svg { flex-shrink: 0; }
`;

const ChunkLoading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.75rem;
  padding: 10px 12px;
  svg { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

// --- Full document modal ---

const DocBox = styled.div`
  background: #0d0d0d;
  border: 1px solid rgba(91, 157, 217, 0.3);
  border-radius: 8px;
  max-width: 720px;
  width: 94vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  color: #fff;
`;

const DocHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
`;

const DocTitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: #5B9DD9;
  line-height: 1.4;
`;

const DocClose = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  padding: 2px;
  flex-shrink: 0;
  &:hover { color: #fff; }
  svg { width: 18px; height: 18px; }
`;

const DocBody = styled.div`
  overflow-y: auto;
  flex: 1;
  padding: 0;
`;

const DocChunk = styled.div`
  font-size: 0.82rem;
  line-height: 1.65;
  padding: 12px 20px;
  color: ${props => props.$isTarget ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.5)'};
  background: ${props => props.$isTarget ? 'rgba(91, 157, 217, 0.1)' : 'transparent'};
  border-left: 3px solid ${props => props.$isTarget ? '#5B9DD9' : 'transparent'};
  ${props => props.$sectionBreak ? `border-top: 1px solid rgba(255, 255, 255, 0.06);` : ''}

  h1, h2, h3, h4 {
    color: rgba(255, 255, 255, 0.85);
    margin: 0.8em 0 0.3em;
    font-size: 1em;
    &:first-child { margin-top: 0; }
  }
  h1 { font-size: 1.15em; }
  h2 { font-size: 1.05em; }
  p { margin: 0.4em 0; }
  ul, ol { margin: 0.3em 0; padding-left: 1.4em; }
  li { margin: 0.15em 0; }
  blockquote {
    border-left: 2px solid rgba(255,255,255,0.15);
    margin: 0.4em 0; padding: 0.2em 0.8em;
    color: rgba(255,255,255,0.45);
  }
  strong { color: rgba(255,255,255,0.8); }
  table { border-collapse: collapse; margin: 0.5em 0; width: 100%; font-size: 0.9em; }
  th, td { border: 1px solid rgba(255,255,255,0.1); padding: 4px 8px; text-align: left; }
  th { background: rgba(255,255,255,0.05); color: rgba(255,255,255,0.7); }
`;

const DocSectionHeading = styled.div`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.25);
  padding: 12px 20px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
`;

const DocLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.85rem;
  padding: 40px 20px;
  svg { animation: spin 1s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

// --- Color helpers ---

function researchColor(v) {
  if (v > 0) return '#4CAF50';
  if (v < 0) return '#E94B6E';
  return 'rgba(255,255,255,0.35)';
}

function qualityColor(v) {
  if (v >= 70) return '#4CAF50';
  if (v >= 40) return '#81C784';
  return '#E94B6E';
}

/**
 * AIScorePanel — Tabbed scores + references panel.
 *
 * Three tabs: Tutkimustuki (-100..+100), Osuvuus (0..100), Liitteet (sources).
 * Click ref → inline chunk context expands. "Avaa lähde" → full doc modal scrolled to ref.
 */
export default function AIScorePanel({ scores, evaluation, sources = [], defaultTab = null, tagVariant = 'dark' }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(defaultTab);
  // Inline expansion: which ref index is expanded
  const [expandedIdx, setExpandedIdx] = useState(null);
  const [chunkContext, setChunkContext] = useState(null);
  const [chunkLoading, setChunkLoading] = useState(false);
  // Full document modal
  const [docModal, setDocModal] = useState(null); // { documentId, targetChunkId, title }
  const [docData, setDocData] = useState(null);
  const [docLoading, setDocLoading] = useState(false);
  const targetChunkRef = useRef(null);

  const expandedSource = expandedIdx != null ? sources[expandedIdx] : null;

  // Fetch surrounding chunks when a ref is expanded inline
  useEffect(() => {
    if (!expandedSource?.chunkId) {
      setChunkContext(null);
      return;
    }
    setChunkLoading(true);
    setChunkContext(null);
    fetch(API_ENDPOINTS.RAG_CHUNK_CONTEXT(expandedSource.chunkId))
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.chunks?.length > 0) setChunkContext(data.chunks);
      })
      .catch(() => {})
      .finally(() => setChunkLoading(false));
  }, [expandedSource?.chunkId]);

  // Fetch full document when doc modal opens
  useEffect(() => {
    if (!docModal?.documentId) {
      setDocData(null);
      return;
    }
    setDocLoading(true);
    setDocData(null);
    fetch(API_ENDPOINTS.RAG_DOCUMENT(docModal.documentId))
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setDocData(data); })
      .catch(() => {})
      .finally(() => setDocLoading(false));
  }, [docModal?.documentId]);

  // Scroll to target chunk once doc loads
  useEffect(() => {
    if (docData && targetChunkRef.current) {
      targetChunkRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [docData]);

  const handleRefClick = useCallback((idx) => {
    setExpandedIdx(prev => prev === idx ? null : idx);
  }, []);

  const handleOpenDoc = useCallback((source) => {
    if (!source.documentId) return;
    setDocModal({
      documentId: source.documentId,
      targetChunkId: source.chunkId,
      title: source.citation || source.title || 'Tuntematon lähde',
    });
  }, []);

  const tagEval = extractTagEvaluation(evaluation || scores);
  const legacyScores = !tagEval && hasLegacyNumericScores(scores) ? scores : null;

  if (!tagEval && !legacyScores && (!sources || sources.length === 0)) return null;

  const hasResearchValue = typeof legacyScores?.research_support === 'number' && Number.isFinite(legacyScores.research_support);
  const researchVal = hasResearchValue ? legacyScores.research_support : 0;
  const qualityVal = legacyScores?.quality ?? 0;

  const handleTabClick = (tab) => {
    setActiveTab(prev => prev === tab ? null : tab);
    setExpandedIdx(null);
  };

  return (
    <Container>
      {(Array.isArray(tagEval?.tags) || tagEval?.research_stance || tagEval?.contradicts_research) && (
        <EvaluationTagChips
          tags={tagEval?.tags || []}
          contradictsResearch={tagEval?.contradicts_research}
          researchStance={tagEval?.research_stance}
          variant={tagVariant}
          noTopMargin
        />
      )}
      <TabRow style={(Array.isArray(tagEval?.tags) || tagEval?.research_stance) ? { marginTop: 10 } : undefined}>
        {legacyScores && (
          <Tab $active={activeTab === 'research'} onClick={() => handleTabClick('research')}>
            <BookOpen size={13} style={{ flexShrink: 0 }} /> {t('aiScorePanel.researchSupport')}
            <BarInline>
              {hasResearchValue && <BarCenterLine />}
              {hasResearchValue && <BarFillInline $bipolar $value={researchVal} />}
            </BarInline>
            <ValueBadge $color={hasResearchValue ? researchColor(researchVal) : 'rgba(255,255,255,0.45)'}>
              {hasResearchValue ? `${researchVal > 0 ? '+' : ''}${researchVal}` : '?'}
            </ValueBadge>
            <ChevronDown className="chevron" />
          </Tab>
        )}

        {legacyScores && (
          <Tab $active={activeTab === 'quality'} onClick={() => handleTabClick('quality')}>
            <Target size={13} style={{ flexShrink: 0 }} /> {t('aiScorePanel.relevance')}
            <BarInline>
              <BarFillInline $value={qualityVal} />
            </BarInline>
            <ValueBadge $color={qualityColor(qualityVal)}>
              {qualityVal}
            </ValueBadge>
            <ChevronDown className="chevron" />
          </Tab>
        )}

        {sources.length > 0 && (
          <Tab $active={activeTab === 'refs'} onClick={() => handleTabClick('refs')}>
            <BookOpen size={12} />
            {t('aiScorePanel.attachmentsCount', { count: sources.filter(s => s.type !== 'analysis').length || sources.length })}
            <ChevronDown className="chevron" />
          </Tab>
        )}
      </TabRow>

      {activeTab && (
        <ContentArea>
          {activeTab === 'research' && legacyScores?.research_support_reason && (
            <ReasonText>{legacyScores.research_support_reason}</ReasonText>
          )}
          {activeTab === 'quality' && legacyScores?.quality_reason && (
            <ReasonText>{legacyScores.quality_reason}</ReasonText>
          )}
          {activeTab === 'refs' && (() => {
            // Group sources by tier (default 'rag' for legacy entries). Render
            // each tier as its own subsection so web sources visibly separate
            // from vetted research. Within a section, RAG keeps its click-to-
            // open doc behavior; web sources open their URL.
            const grouped = {};
            sources.forEach((s, idx) => {
              const tier = s.tier || 'rag';
              (grouped[tier] ||= []).push({ ...s, _origIdx: idx });
            });
            const tiers = TIER_ORDER.filter(t => grouped[t]?.length);
            return (
              <>
                {tiers.map((tierId, tIdx) => {
                  const meta = getTier(tierId);
                  const items = grouped[tierId];
                  return (
                    <React.Fragment key={tierId}>
                      {(tiers.length > 1 || tierId !== 'rag') && (
                        <div style={{ marginTop: tIdx === 0 ? 0 : 10, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <SourceTag tier={tierId} count={items.length} />
                          {meta.disclaimerKey && (
                            <span style={{ fontSize: '0.68rem', fontStyle: 'italic', color: 'rgba(255,255,255,0.5)' }}>
                              {t(meta.disclaimerKey)}
                            </span>
                          )}
                        </div>
                      )}
                      <RefList>
                        {items.map((source) => {
                          const i = source._origIdx;
                          const marker = source.marker || `[${i + 1}]`;
                          const isAnalysis = source.type === 'analysis';
                          const prevSource = i > 0 ? sources[i - 1] : null;
                          const showDivider = isAnalysis && prevSource?.type !== 'analysis' && tierId === 'rag';
                          const rawCitation = source.title || source.citation || 'Tuntematon lähde';
                          const cleanTitle = rawCitation
                            .replace(/\.[a-z]{2,4}\s*\|/gi, ' |')
                            .split('|')[0].trim()
                            .replace(/_/g, ' ');
                          const pageMatch = rawCitation.match(/p\.\s*(\d+)/);
                          const isWeb = tierId === 'web' && source.url;
                          const clickable = isWeb || !!source.documentId;
                          const onClick = isWeb
                            ? () => window.open(source.url, '_blank', 'noopener,noreferrer')
                            : (source.documentId ? () => handleOpenDoc(source) : null);
                          return (
                            <React.Fragment key={i}>
                              {showDivider && <RefDivider>Arvioinnissa käytetyt lähteet</RefDivider>}
                              <RefItem
                                onClick={onClick || (() => {})}
                                style={{ cursor: clickable ? 'pointer' : 'default', opacity: isAnalysis ? 0.6 : 1 }}
                              >
                                <RefNumber $analysis={isAnalysis} style={isWeb ? { color: meta.color } : undefined}>
                                  {isAnalysis ? '·' : marker}
                                </RefNumber>
                                <RefCitation>{cleanTitle}{pageMatch ? `, s. ${pageMatch[1]}` : ''}</RefCitation>
                                {isWeb
                                  ? <ExternalLink size={11} style={{ flexShrink: 0, opacity: 0.5, color: meta.color }} />
                                  : (source.documentId && <BookOpen size={11} style={{ flexShrink: 0, opacity: 0.4 }} />)
                                }
                              </RefItem>
                            </React.Fragment>
                          );
                        })}
                      </RefList>
                    </React.Fragment>
                  );
                })}
              </>
            );
          })()}
        </ContentArea>
      )}

      {/* Full document modal */}
      {docModal && (
        <ModalWrapper visible={!!docModal} onClose={() => setDocModal(null)} showCloseButton={false} zIndex={2000} blur={0} opacity={0.7}>
          <DocBox>
            <DocHeader>
              <DocTitle>{(docModal.title || '').split('|')[0].trim().replace(/_/g, ' ')}</DocTitle>
              <DocClose onClick={() => setDocModal(null)}><X /></DocClose>
            </DocHeader>
            <DocBody>
              {docLoading ? (
                <DocLoading><Loader2 size={16} /> Ladataan dokumentti...</DocLoading>
              ) : docData ? (
                docData.chunks.map((chunk, i) => {
                  const isTarget = chunk.id === docModal.targetChunkId;
                  const prevChunk = i > 0 ? docData.chunks[i - 1] : null;
                  const sectionChange = chunk.section_heading && chunk.section_heading !== prevChunk?.section_heading;
                  const cleaned = cleanChunkContent(chunk.content);
                  if (!cleaned) return null;
                  return (
                    <React.Fragment key={chunk.id}>
                      {sectionChange && (
                        <DocSectionHeading>{chunk.section_heading}</DocSectionHeading>
                      )}
                      <DocChunk
                        ref={isTarget ? targetChunkRef : undefined}
                        $isTarget={isTarget}
                        $sectionBreak={!sectionChange && i > 0}
                      >
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{cleaned}</ReactMarkdown>
                      </DocChunk>
                    </React.Fragment>
                  );
                })
              ) : (
                <DocLoading>Dokumenttia ei löytynyt.</DocLoading>
              )}
            </DocBody>
          </DocBox>
        </ModalWrapper>
      )}
    </Container>
  );
}
